/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BaseButton from "components/common/Button/Base";
import { PALETTE } from "styles/base/variables";
import { KakaoNotificationLog } from "models/paymentNotice.model";
import { getPaymentNoticeKakaoHistory } from "pages/remotes";
import { fillTemplate, KAKAO_TEMPLATES } from "./templates";

interface KakaoHistoryDialogProps {
  isOpen: boolean;
  businessId: string | null;
  businessName: string;
  // 급여 지급월 (YYYY-MM). 백엔드(§2.8)가 사업자별 귀속월로 변환해 조회한다.
  period: string;
  onClose: () => void;
}

const fmtDateTime = (v: string) => (v ? v.replace("T", " ").slice(0, 16) : "-");

// 숫자 11자리는 010-1234-5678 형태로, 그 외에는 원본 그대로 표시
const fmtMobileNo = (v: string | null) => {
  if (!v) return "";
  const digits = v.replace(/[^0-9]/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return v;
};

function KakaoBubble({ log }: { log: KakaoNotificationLog }) {
  const tpl = KAKAO_TEMPLATES[log.templateId];
  const emphasis = tpl?.emphasis;
  const title = tpl ? fillTemplate(tpl.title, log.variables) : "알림톡";
  const body = tpl
    ? fillTemplate(tpl.body, log.variables)
    : Object.entries(log.variables)
        .map(([k, v]) => `${k} = ${v}`)
        .join("\n");
  const footer = tpl?.footer;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: ${PALETTE.textMid};
        `}
      >
        <span>{fmtDateTime(log.sentAt)} 발송</span>
        {log.recipientMobileNo && (
          <span
            css={css`
              padding-left: 8px;
              border-left: 1px solid ${PALETTE.border};
            `}
          >
            수신 {fmtMobileNo(log.recipientMobileNo)}
          </span>
        )}
      </div>
      <div
        css={css`
          width: 300px;
          max-width: 100%;
          border-radius: 12px;
          overflow: hidden;
          background: ${PALETTE.light};
          border: 1px solid ${PALETTE.border};
        `}
      >
        <div
          css={css`
            background: #fae100;
            color: #3a1d1d;
            font-size: 13px;
            font-weight: 700;
            padding: 8px 14px;
          `}
        >
          알림톡 도착
        </div>
        <div
          css={css`
            padding: 14px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          `}
        >
          {emphasis && (
            <div
              css={css`
                font-size: 12px;
                color: ${PALETTE.textMid};
              `}
            >
              {emphasis}
            </div>
          )}
          <div
            css={css`
              font-size: 16px;
              font-weight: 700;
              color: ${PALETTE.text};
            `}
          >
            {title}
          </div>
          <div
            css={css`
              border-top: 1px solid ${PALETTE.borderLight};
            `}
          />
          <div
            css={css`
              font-size: 13px;
              line-height: 1.6;
              color: ${PALETTE.text};
              white-space: pre-wrap;
              word-break: keep-all;
            `}
          >
            {body}
          </div>
          {footer && (
            <div
              css={css`
                font-size: 12px;
                color: ${PALETTE.textMid};
                white-space: pre-wrap;
              `}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
      {!tpl && (
        <div
          css={css`
            font-size: 11px;
            color: ${PALETTE.textMid};
          `}
        >
          알 수 없는 템플릿 ({log.templateId})
        </div>
      )}
    </div>
  );
}

function KakaoHistoryDialog({
  isOpen,
  businessId,
  businessName,
  period,
  onClose
}: KakaoHistoryDialogProps) {
  const [logs, setLogs] = useState<KakaoNotificationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !businessId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLogs([]);
    getPaymentNoticeKakaoHistory(businessId, period)
      .then((res) => {
        if (cancelled) return;
        setLogs(res.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.response?.data?.message ||
            "발송 이력을 불러오는 중 오류가 발생했습니다."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, businessId, period]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        css={css`
          font-size: 16px;
          font-weight: 700;
        `}
      >
        납부서 알림톡 발송 내역
        <span
          css={css`
            font-weight: 400;
            font-size: 12px;
            color: ${PALETTE.textMid};
            margin-left: 8px;
          `}
        >
          {businessName} · {period}
        </span>
        <IconButton
          onClick={onClose}
          css={css`
            position: absolute;
            right: 8px;
            top: 8px;
          `}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {!loading && error && (
          <div
            css={css`
              font-size: 13px;
              color: ${PALETTE.error};
              padding: 16px 0;
            `}
          >
            {error}
          </div>
        )}
        {!loading && !error && logs.length === 0 && (
          <div
            css={css`
              font-size: 13px;
              color: ${PALETTE.textMid};
              padding: 16px 0;
            `}
          >
            이 지급월로 발송한 알림톡이 없습니다.
          </div>
        )}
        {!loading && !error && logs.length > 0 && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              padding: 8px 0;
            `}
          >
            {logs.map((log, i) => (
              <KakaoBubble key={`${log.sentAt}-${i}`} log={log} />
            ))}
          </div>
        )}
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px;
        `}
      >
        <BaseButton
          variant="contained"
          color={PALETTE.primary}
          onClick={onClose}
        >
          닫기
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
}

export default KakaoHistoryDialog;
